export interface TranscriptExcerpt {
  /** e.g. "03:12" — absent when the model couldn't provide one. */
  timestamp?: string;
  text: string;
}

const BULLET_PREFIX = /^[-•*]\s+/;
/** Bracketed clock timestamp only — `m:ss`, `mm:ss`, or `h:mm:ss` (not e.g. `[Note]`). */
const TIMESTAMP_PREFIX = /^\[(\d+:\d{2}(?::\d{2})?)\]\s*/;

const WRAPPING_QUOTE_PAIRS: Array<[string, string]> = [
  ['"', '"'],
  ['“', '”'],
  ["'", "'"],
];

const hasWrappingQuotes = (value: string): boolean => {
  for (const [open, close] of WRAPPING_QUOTE_PAIRS) {
    if (value.startsWith(open) && value.endsWith(close) && value.length >= 2) {
      return true;
    }
  }
  return false;
};

const stripWrappingQuotes = (value: string): string => {
  for (const [open, close] of WRAPPING_QUOTE_PAIRS) {
    if (value.startsWith(open) && value.endsWith(close) && value.length >= 2) {
      return value.slice(open.length, -close.length).trim();
    }
  }
  return value;
};

/** True when a line looks like a transcript quote (`[MM:SS]` and/or wrapping quotes). */
export const isQuoteShapedLine = (line: string): boolean => {
  const trimmed = line.trim();
  if (!trimmed) return false;

  const rest = trimmed.replace(BULLET_PREFIX, '');
  if (TIMESTAMP_PREFIX.test(rest)) return true;

  return hasWrappingQuotes(rest.trim());
};

/** True when the model returned at least one quote-shaped transcript line. */
export const hasQuoteShapedTranscriptExcerpts = (text: string): boolean =>
  text.split('\n').some(isQuoteShapedLine);

/**
 * Parses a View-transcript-source response into structured excerpts. Lines
 * look like `[MM:SS] "quote"` per the prompt contract, but the parser is
 * tolerant: bullets, missing timestamps, and unquoted lines all still parse —
 * worst case a prose response comes back as a single untimestamped excerpt.
 */
export const parseTranscriptExcerpts = (text: string): TranscriptExcerpt[] => {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      let rest = line.replace(BULLET_PREFIX, '');
      const timestampMatch = TIMESTAMP_PREFIX.exec(rest);
      let timestamp: string | undefined;
      if (timestampMatch) {
        timestamp = timestampMatch[1];
        rest = rest.slice(timestampMatch[0].length);
      }
      return { timestamp, text: stripWrappingQuotes(rest.trim()) };
    })
    .filter((excerpt) => excerpt.text.length > 0);
};

/** Quote-shaped lines only — used for View in Transcript evidence and preview. */
export const parseQuoteShapedTranscriptExcerpts = (text: string): TranscriptExcerpt[] =>
  parseTranscriptExcerpts(text.split('\n').filter(isQuoteShapedLine).join('\n'));

/** True when at least one parsed excerpt includes a `[MM:SS]` timestamp. */
export const hasTimestampedTranscriptExcerpts = (text: string): boolean =>
  parseTranscriptExcerpts(text).some((excerpt) => excerpt.timestamp != null);
